import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getApiProxyBaseUrl } from "../runtimeConfig";

const AuthContext = createContext(null);

const TOKEN_KEY = "mgdr_jwt_token";
const DEFAULT_AUTHENTICATED_ROUTE = "/rulesView";

const API_BASE = `${getApiProxyBaseUrl()}/api`;

function parseJwtPayload(token) {
    try {
        const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
}

function isTokenValid(token) {
    if (!token) return false;
    const payload = parseJwtPayload(token);
    if (!payload?.exp) return false;
    return Date.now() < payload.exp * 1000;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        const token = sessionStorage.getItem(TOKEN_KEY);
        if (isTokenValid(token)) {
            const payload = parseJwtPayload(token);
            if (mountedRef.current) {
                setUser(payload.preferred_username || payload.sub || null);
                setIsAuthenticated(true);
            }
        } else {
            sessionStorage.removeItem(TOKEN_KEY);
        }
        if (mountedRef.current) setLoading(false);
        return () => { mountedRef.current = false; };
    }, []);

    const login = async (username, password) => {
        const response = await axios.post(`${API_BASE}/v1/oauth/token`, { username, password });
        const { accessToken, username: returnedUsername } = response.data;
        sessionStorage.setItem(TOKEN_KEY, accessToken);
        setUser(returnedUsername || username);
        setIsAuthenticated(true);
        return DEFAULT_AUTHENTICATED_ROUTE;
    };

    const logout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setIsAuthenticated(false);
        window.location.replace("/login");
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
    return ctx;
}
